import React, { useContext, useMemo, type PropsWithChildren } from 'react';
import { CalendarEvent } from '@yi/core/src/add-to-calendar';

import { useReportUnexpectedError } from '~/src/common/reporters/useReportUnexpectedError';

import type { PracticeDetails } from './types';

type Value = {
  practiceDetails: PracticeDetails;
};
const AddToCalendarDataContext = React.createContext<Value | undefined>(undefined);

interface Props {
  practiceDetails: PracticeDetails & { id: string };
}
export const AddToCalendarDataProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  practiceDetails,
}) => {
  const report = useReportUnexpectedError();
  const value = useMemo(() => {
    const { full_url, id, ...restPracticeDetails } = practiceDetails;

    const calendarEvent = new CalendarEvent({
      contentId: id,
      fullUrl: full_url,
    });
    const fullUrlWithDeepLink = calendarEvent.deepLinkUrl();
    if (fullUrlWithDeepLink instanceof Error) {
      report({ error: fullUrlWithDeepLink });
      return {
        practiceDetails: {
          ...restPracticeDetails,
          full_url,
        },
      };
    }
    return {
      practiceDetails: {
        ...restPracticeDetails,
        full_url: fullUrlWithDeepLink,
      },
    };
  }, [practiceDetails, report]);

  return (
    <AddToCalendarDataContext.Provider value={value}>{children}</AddToCalendarDataContext.Provider>
  );
};

export const useAddToCalendarData = () => {
  const value = useContext(AddToCalendarDataContext);
  if (!value) throw new Error('Missing AddToCalendarDataContext Provider');
  return value;
};
